// Core
import React, {
  useCallback,
  useEffect,
  useState,
} from 'react';

// Libraries
import { Trans, useTranslation } from 'react-i18next';
import { useTheme } from 'styled-components';
import {
  useNavigate,
  useSearchParams,
  NavigateFunction,
} from 'react-router-dom';
import { jwtDecode } from 'jwt-decode';

// Plugins
import {
  requestVantageApi,
  RequestMethod,
  isHTTPResponseError,
} from '../../plugins/request';

// Store
import {
  setAuthToken,
  setUser,
} from '../../store/session.store';

// Hooks
import { useBreakpoint } from '../../hooks/useBreakpoint';

// Config
import { DOCUMENT_EULA_URL, DOCUMENT_PRIVACY_POLICY_URL } from '../../config/documents';

// Layout
import { Alert, AlertType } from '../../layout/elements/Alert';
import {
  Button,
  ButtonColor,
  ButtonSize,
  ButtonType,
} from '../../layout/elements/Button';
import { Checkbox, CheckboxSize } from '../../layout/elements/Checkbox';
import { Input, InputType } from '../../layout/elements/Input';
import {
  LinkTo,
  LinkToColor,
  LinkToSize,
  LinkToType,
  LinkToWeight,
} from '../../layout/elements/LinkTo';
import { LeftPanelImageRight } from '../../layout/LeftPanelImageRight';
import { Loader, LoaderSize } from '../../layout/elements/Loader';
import {
  Paragraph,
  ParagraphAlign,
  ParagraphSize,
  ParagraphWeight,
} from '../../layout/elements/Paragraph';
import {
  Title,
  TitleAlign,
  TitleSize,
  TitleWeight,
} from '../../layout/elements/Title';
import {
  AlertLine,
  ArrowLeft,
  CheckCircleFill,
} from '../../layout/icons';

// Style
import {
  AlertContainer,
  BackToLoginContainer,
  Description,
  ErrorList,
  FormContentContainer,
  InputPasswordContainer,
  RegisterPasswordContentContainer,
  TermsCheckboxContainer,
  TitleContainer,
  TrademarkSymbol,
} from './RegisterPassword.style';

// Types
import { Theme } from '../../types/theme';
import {
  LoginAuthResponse,
  User,
  UserStatus,
  UserType,
} from '../../types/global';

function EulaLink({ children }: { children?: React.ReactNode }): JSX.Element {
  return (
    <LinkTo
      color={LinkToColor.primary}
      target={DOCUMENT_EULA_URL}
      type={LinkToType.link}
      weight={LinkToWeight.medium}
      size={LinkToSize.sm}
      newTab
    >
      {children}
    </LinkTo>
  );
}

function PrivacyLink({ children }: { children?: React.ReactNode }): JSX.Element {
  return (
    <LinkTo
      color={LinkToColor.primary}
      target={DOCUMENT_PRIVACY_POLICY_URL}
      type={LinkToType.link}
      weight={LinkToWeight.medium}
      size={LinkToSize.sm}
      newTab
    >
      {children}
    </LinkTo>
  );
}

function RegisterPassword(): JSX.Element {
  // Dependencies
  const { t } = useTranslation();
  const navigate: NavigateFunction = useNavigate();
  const theme: Theme = useTheme();
  const [searchParams] = useSearchParams();
  const tokenFromUrl: string | null = searchParams.get('token');
  const { isXs, isSm, isMd } = useBreakpoint();

  /* ***********************************************************************************************
  ***************************************** LOCAL STATES *******************************************
  *********************************************************************************************** */

  interface RegisterPasswordFormState {
    newPassword: string | null;
    confirmPassword: string | null;
    invalidPasswordErrorMessage: string | null;
    isLoading: number | null | true;
    isValidatingToken: boolean;
    tokenValidated: boolean;
    passwordCreated: boolean;
    userEmail: string | null;
    userStatus: UserStatus | null;
    acceptedTerms: boolean;
  }

  const initialRegisterPasswordFormState: RegisterPasswordFormState = {
    newPassword: null,
    confirmPassword: null,
    invalidPasswordErrorMessage: null,
    isLoading: null,
    isValidatingToken: false,
    tokenValidated: false,
    passwordCreated: false,
    userEmail: null,
    userStatus: null,
    acceptedTerms: false,
  };

  const [
    registerPasswordFormState,
    setRegisterPasswordFormState,
  ] = useState<RegisterPasswordFormState>(
    initialRegisterPasswordFormState,
  );

  /* ***********************************************************************************************
  ******************************************* METHODS **********************************************
  *********************************************************************************************** */

  /**
   * Validates the token by calling the backend API
   */
  const validateToken = useCallback(async (): Promise<void> => {
    if (
      !registerPasswordFormState.isValidatingToken
      && !registerPasswordFormState.tokenValidated
      && tokenFromUrl
    ) {
      setRegisterPasswordFormState((prevState: RegisterPasswordFormState) => ({
        ...prevState,
        isValidatingToken: true,
      }));

      try {
        const validationResponse = await requestVantageApi.users({
          method: RequestMethod.POST,
          path: '/auth/validate-token',
          body: { token: tokenFromUrl },
          handleErrors: false,
        });

        // Token is valid - extract user data from response
        const userEmail: string = validationResponse?.email;
        const userStatus: UserStatus = validationResponse?.status;

        setRegisterPasswordFormState((prevState: RegisterPasswordFormState) => ({
          ...prevState,
          isValidatingToken: false,
          tokenValidated: true,
          userEmail,
          userStatus,
        }));
      } catch (error: unknown) {
        // Token validation failed, redirect to login with error message
        let errorType: string = 'invalidToken';

        if (isHTTPResponseError(error)) {
          if (error.response.status === 400) {
            errorType = 'invalidToken';
          } else if (error.response.status === 404) {
            errorType = 'tokenNotFound';
          } else if (error.response.status === 422) {
            errorType = 'tokenExpired';
          }
        }

        setRegisterPasswordFormState((prevState: RegisterPasswordFormState) => ({
          ...prevState,
          isValidatingToken: false,
          tokenValidated: false,
        }));

        // Redirect to login with error parameter
        navigate(`/?error=${errorType}`);
      }
    }
  }, [
    tokenFromUrl,
    navigate,
    registerPasswordFormState.isValidatingToken,
    registerPasswordFormState.tokenValidated,
  ]);

  /**
   * Validates the password and returns only the error messages for failing validations.
   * @param password - The password to validate
   * @param confirmPassword - The confirmation password to validate
   * @returns A string with all error messages separated by newline, or null if valid
   */
  const validatePasswordRequirements = (
    password: string | null,
    confirmPassword: string | null,
  ): string | null => {
    const errors: string[] = [];

    // Only perform further validations if both fields are filled
    if (password && confirmPassword) {
      if (password !== confirmPassword) {
        errors.push(t('view.registerPassword.doNotMatch'));
      }
      if (!/[!@#$%^&*(),.?":{}|<>]/.test(password)) {
        errors.push(t('view.registerPassword.missingSpecialChar'));
      }
      if (!/[a-z]/.test(password)) {
        errors.push(t('view.registerPassword.missingLowercaseLetter'));
      }
      if (!/[A-Z]/.test(password)) {
        errors.push(t('view.registerPassword.missingUppercaseLetter'));
      }
      if (!/[0-9]/.test(password)) {
        errors.push(t('view.registerPassword.missingNumber'));
      }
      if (password.length < 8) {
        errors.push(t('view.registerPassword.tooShort'));
      }
    }

    return errors.length > 0 ? errors.join('\n') : null;
  };

  /**
   * Performs automatic login after password creation
   */
  const performAutoLogin = useCallback(async (): Promise<void> => {
    if (!registerPasswordFormState.userEmail || !registerPasswordFormState.newPassword) {
      return;
    }

    setRegisterPasswordFormState((prevState: RegisterPasswordFormState) => ({
      ...prevState,
      isLoggingIn: true,
    }));

    try {
      // Perform login with the new password
      const authResponse: LoginAuthResponse = await requestVantageApi.users({
        method: RequestMethod.POST,
        path: '/auth/authenticate',
        body: {
          email: registerPasswordFormState.userEmail,
          password: registerPasswordFormState.newPassword,
        },
        handleErrors: false,
      });

      const { token } = authResponse;

      // Decode token and set user data
      const decodedToken: { user: string } = jwtDecode(token);
      const decodedUser: User = JSON.parse(decodedToken.user);
      setUser(decodedUser);
      setAuthToken(token);

      // Navigate based on user type
      if (decodedUser.type === UserType.admin || decodedUser.type === UserType.service) {
        navigate('/');
      } else if (decodedUser.type === UserType.customer) {
        navigate('/plant-management');
      }
    } catch (error: unknown) {
      // If auto-login fails, redirect to login page
      navigate('/');
    }
  }, [
    registerPasswordFormState.userEmail,
    registerPasswordFormState.newPassword,
    navigate,
  ]);

  /**
   *
   * @returns {Promise<void>} A promise that resolves when the registration process is complete.
   */
  const isFirstAccess: boolean = registerPasswordFormState.userStatus === UserStatus.invited;

  const submitRegisterPasswordForm = async (): Promise<void> => {
    if (isFirstAccess && !registerPasswordFormState.acceptedTerms) {
      return;
    }
    if (!registerPasswordFormState.newPassword || !registerPasswordFormState.confirmPassword) {
      return;
    }

    const errorMessage: string | null = validatePasswordRequirements(
      registerPasswordFormState.newPassword,
      registerPasswordFormState.confirmPassword,
    );

    if (errorMessage) {
      return;
    }

    setRegisterPasswordFormState((prevState: RegisterPasswordFormState) => ({
      ...prevState,
      isLoading: true,
    }));

    try {
      await requestVantageApi.users({
        method: RequestMethod.PUT,
        path: '/auth/register-password',
        body: {
          password: registerPasswordFormState.newPassword,
          token: tokenFromUrl,
          ...(isFirstAccess && { acceptedTerms: registerPasswordFormState.acceptedTerms }),
        },
        handleErrors: false,
      });

      // Show success state and perform auto-login
      setRegisterPasswordFormState((prevState: RegisterPasswordFormState) => ({
        ...prevState,
        isLoading: null,
        passwordCreated: true,
      }));

      // Perform automatic login with captured email
      await performAutoLogin();
    } catch (error: unknown) {
      // Reset loading state on error
      setRegisterPasswordFormState((prevState: RegisterPasswordFormState) => ({
        ...prevState,
        isLoading: null,
      }));
    }
  };

  /**
   * Updates a field in the register password form state.
   * As soon as one of the password fields is updated, the password is validated
   * and validation errors are shown.
   * @param field - The name of the field to update
   * @param value - The new value for the field
   */
  const updateRegisterPasswordField = (
    field: keyof RegisterPasswordFormState,
    value?: string | boolean | null | number,
  ): void => {
    setRegisterPasswordFormState((prevState: RegisterPasswordFormState) => {
      const newState = {
        ...prevState,
        [field]: value as string | boolean | null,
      };

      if (field === 'newPassword' || field === 'confirmPassword') {
        newState.invalidPasswordErrorMessage = validatePasswordRequirements(
          newState.newPassword,
          newState.confirmPassword,
        );
      }

      return newState;
    });
  };

  const toggleAcceptedTerms = (): void => {
    setRegisterPasswordFormState((prevState: RegisterPasswordFormState) => ({
      ...prevState,
      acceptedTerms: !prevState.acceptedTerms,
    }));
  };

  /**
   * Navigates back to the login page.
   */
  const navigateBackToLogin = (): void => {
    navigate('/');
  };

  /**
   * Determines if the register password form submit button should be disabled.
   * Button is only enabled when all password requirements are met.
   * @type {boolean}
   */
  const isDisabledToSubmitRegisterPasswordForm: boolean = (
    registerPasswordFormState.invalidPasswordErrorMessage !== null
    || !registerPasswordFormState.newPassword
    || !registerPasswordFormState.confirmPassword
    || (isFirstAccess && !registerPasswordFormState.acceptedTerms)
  );

  /* ***********************************************************************************************
  ************************************* COMPONENT HANDLING *****************************************
  *********************************************************************************************** */

  // Check for token in URL and validate it
  useEffect(() => {
    if (tokenFromUrl) {
      validateToken();
    }
  }, [tokenFromUrl, validateToken]);

  // Show loading while validating token
  if (registerPasswordFormState.isValidatingToken) {
    return (
      <LeftPanelImageRight>
        <RegisterPasswordContentContainer>
          <FormContentContainer>
            <TitleContainer>
              <Title
                align={TitleAlign.center}
                size={(isXs || isSm || isMd) ? TitleSize.xxs : TitleSize.xs}
                weight={TitleWeight.bold}
              >
                {t('view.registerPassword.title')}
              </Title>
            </TitleContainer>
            <Description>
              <Loader size={LoaderSize.lg} />
            </Description>
            <Description>
              <Paragraph
                align={ParagraphAlign.center}
                color={theme.color.passwordRecovery.descriptionTextColor}
              >
                {t('common.loader')}
              </Paragraph>
            </Description>
          </FormContentContainer>
        </RegisterPasswordContentContainer>
      </LeftPanelImageRight>
    );
  }

  return (
    <LeftPanelImageRight>
      <RegisterPasswordContentContainer>
        <FormContentContainer>
          <TitleContainer>
            <Title
              align={TitleAlign.center}
              size={(isXs || isSm || isMd) ? TitleSize.xxs : TitleSize.xs}
              weight={TitleWeight.bold}
            >
              {t('view.registerPassword.title')}
            </Title>
          </TitleContainer>

          {registerPasswordFormState.passwordCreated ? (
            <>
              <AlertContainer>
                <Alert
                  closable={false}
                  description={`${t('view.registerPassword.passwordCreatedSuccess')}`}
                  icon={<CheckCircleFill />}
                  testId="successAlert"
                  type={AlertType.success}
                />
              </AlertContainer>

              <Description>
                <Loader size={LoaderSize.lg} />
              </Description>

              <Description>
                <Paragraph
                  align={ParagraphAlign.center}
                  color={theme.color.passwordRecovery.descriptionTextColor}
                >
                  {`${t('view.registerPassword.redirecting')} Vantage`}
                  <TrademarkSymbol>®</TrademarkSymbol>
                  Cloud.
                </Paragraph>
              </Description>
            </>
          ) : (
            <>
              <Description>
                <Paragraph
                  align={ParagraphAlign.center}
                  color={theme.color.passwordRecovery.descriptionTextColor}
                >
                  {t('view.registerPassword.paragraphInfoUpdatePassword')}
                </Paragraph>
              </Description>

              <InputPasswordContainer>
                <Input
                  error={registerPasswordFormState.invalidPasswordErrorMessage !== null}
                  label={`${t('view.registerPassword.newPassword')}`}
                  onChange={(
                    value?: string | number | null,
                  ) => updateRegisterPasswordField('newPassword', value)}
                  placeholder={`${t('view.registerPassword.newPassword')}`}
                  testId="input-new-password"
                  type={InputType.password}
                  value={registerPasswordFormState.newPassword}
                />
                <Input
                  error={registerPasswordFormState.invalidPasswordErrorMessage !== null}
                  label={`${t('view.registerPassword.confirmPassword')}`}
                  onChange={(
                    value?: string | number | null,
                  ) => updateRegisterPasswordField('confirmPassword', value)}
                  placeholder={`${t('view.registerPassword.confirmPassword')}`}
                  testId="input-confirm-password"
                  type={InputType.password}
                  value={registerPasswordFormState.confirmPassword}
                  onPressEnter={
                    !isDisabledToSubmitRegisterPasswordForm
                      ? submitRegisterPasswordForm
                      : undefined
                  }
                />

                {isFirstAccess && (
                  <TermsCheckboxContainer>
                    <Checkbox
                      checked={registerPasswordFormState.acceptedTerms}
                      onChange={toggleAcceptedTerms}
                      size={CheckboxSize.sm}
                      testId="accept-terms-checkbox"
                      supportingText={(
                        <Trans
                          i18nKey="view.registerPassword.acceptTermsText"
                          components={{
                            eula: <EulaLink />,
                            privacy: <PrivacyLink />,
                          }}
                        />
                      )}
                    />
                  </TermsCheckboxContainer>
                )}

                {registerPasswordFormState.invalidPasswordErrorMessage && (
                  <AlertContainer>
                    <Alert
                      icon={<AlertLine />}
                      type={AlertType.error}
                      description={(
                        <div>
                          <div>
                            <Paragraph
                              size={ParagraphSize.sm}
                              weight={ParagraphWeight.bold}
                              color={theme.color.alert.errorText}
                            >
                              {t('view.registerPassword.doesNotMeet')}
                            </Paragraph>
                          </div>
                          <ErrorList>
                            {registerPasswordFormState.invalidPasswordErrorMessage
                              .split('\n')
                              .map((error) => (
                                <li key={error}>
                                  {error}
                                </li>
                              ))}
                          </ErrorList>
                        </div>
                      )}
                      closable={false}
                    />
                  </AlertContainer>
                )}

                <Button
                  size={ButtonSize.lg}
                  loading={registerPasswordFormState.isLoading}
                  disabled={isDisabledToSubmitRegisterPasswordForm}
                  loadingText={`${t('view.registerPassword.creatingPassword')}`}
                  onClick={submitRegisterPasswordForm}
                  testId="create-password-button"
                >
                  {t('view.registerPassword.continue')}
                </Button>

                <BackToLoginContainer>
                  <Button
                    color={ButtonColor.neutral}
                    onClick={navigateBackToLogin}
                    size={ButtonSize.lg}
                    type={ButtonType.link}
                    testId="back-to-login-button"
                  >
                    <ArrowLeft />
                    {t('view.registerPassword.backToLogin')}
                  </Button>
                </BackToLoginContainer>
              </InputPasswordContainer>
            </>
          )}
        </FormContentContainer>
      </RegisterPasswordContentContainer>
    </LeftPanelImageRight>
  );
}

export { RegisterPassword };
